/**
 * textLook.ts — слово владельца 20.10.2026 (задание stylepack/text): вид
 * текста блока, который разборщик повторяет у чужой воронки. Здесь — зеркало
 * границ сервера и накладки стиля для публичной отрисовки.
 *
 * Границы — ЗЕРКАЛО канона сервера `TEXT_LOOK_BOUNDS`
 * (backend/src/ai-funnels/block-schema/text-look.ts); разъезд ловит сторож
 * `text-look.look-ends.spec.ts` — он читает ЭТОТ объект с диска, форму
 * `{ min: N, max: N }` не менять, не обновив его.
 *
 * Нет поля — накладка пустая `{}`: вид решает стиль-пак, как сейчас.
 */
import type { CSSProperties } from 'react';

/** Типы с полями вида текста — зеркало `TEXT_LOOK_BLOCK_TYPES` сервера. */
export const TEXT_LOOK_BLOCK_TYPES: ReadonlySet<string> = new Set([
  'title',
  'sub_title',
  'heading',
  'paragraph',
]);

export const TEXT_LOOK_BOUNDS = {
  lineHeight: { min: 0.8, max: 3 },
  letterSpacing: { min: -5, max: 20 },
  fontWeight: { min: 100, max: 900 },
} as const;

export interface TextLookFields {
  lineHeight?: number;
  letterSpacing?: number;
  fontWeight?: number;
  textTransform?: 'none' | 'uppercase';
}

type Bounds = { min: number; max: number };

/** Число в границах или undefined (нечисло — не в стиль). */
export function clampLook(
  value: unknown,
  bounds: Bounds,
): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  return Math.min(bounds.max, Math.max(bounds.min, value));
}

/** Жирность сотнями в границах 100..900 или undefined. */
export function lookFontWeight(value: unknown): number | undefined {
  const w = clampLook(value, TEXT_LOOK_BOUNDS.fontWeight);
  return w === undefined ? undefined : Math.round(w / 100) * 100;
}

/**
 * Накладка вида текста — спредится ПОСЛЕ решения стиль-пака (интерлиньяж,
 * трекинг и жирность пака или прежние числа отрисовки).
 *
 * Пометка bold у куска текста (RichTextContent) — своё `fontWeight: 700` на
 * куске, оно сильнее жирности блока: блок задаёт вес обычного текста, кусок
 * с bold остаётся 700.
 */
export function textLookOverride(fields: TextLookFields): CSSProperties {
  const out: CSSProperties = {};
  const lineHeight = clampLook(fields.lineHeight, TEXT_LOOK_BOUNDS.lineHeight);
  if (lineHeight !== undefined) out.lineHeight = lineHeight;
  const spacing = clampLook(fields.letterSpacing, TEXT_LOOK_BOUNDS.letterSpacing);
  if (spacing !== undefined) out.letterSpacing = `${spacing}px`;
  const weight = lookFontWeight(fields.fontWeight);
  if (weight !== undefined) out.fontWeight = weight;
  if (fields.textTransform === 'none' || fields.textTransform === 'uppercase') {
    out.textTransform = fields.textTransform;
  }
  return out;
}

/**
 * Коммит 3 задания — вид КНОПКИ: высота и жирность подписи у кнопки «Далее»
 * и кнопки-ссылки. Зеркало `BUTTON_LOOK_BLOCK_TYPES` / `BUTTON_LOOK_BOUNDS`
 * сервера (text-look.ts), тот же сторож text-look.look-ends.spec.ts.
 */
export const BUTTON_LOOK_BLOCK_TYPES: ReadonlySet<string> = new Set([
  'button_next',
  'button_link',
]);

export const BUTTON_LOOK_BOUNDS = {
  buttonHeight: { min: 32, max: 96 },
  fontWeight: { min: 100, max: 900 },
} as const;

export interface ButtonLookFields {
  buttonHeight?: number;
  fontWeight?: number;
}

/**
 * Накладка вида кнопки — спредится ПОСЛЕДНЕЙ в стиль кнопки (после темы,
 * пака и своего кегля). Высота — ровно эта, с учётом рамки и отступов
 * (`border-box`); у `<button>` подпись встаёт по центру сама, у ссылки-кнопки
 * (`<a>`, inline-block) центровку даёт inline-flex. Длинная подпись в две
 * строки при малой высоте может выйти за кнопку — граница 32 px это допускает
 * только при крупном кегле. Пусто — `{}`.
 */
export function buttonLookOverride(
  fields: ButtonLookFields,
  isLink: boolean,
): CSSProperties {
  const out: CSSProperties = {};
  const height = clampLook(fields.buttonHeight, BUTTON_LOOK_BOUNDS.buttonHeight);
  if (height !== undefined) {
    out.height = Math.round(height);
    out.boxSizing = 'border-box';
    if (isLink) {
      out.display = 'inline-flex';
      out.alignItems = 'center';
      out.justifyContent = 'center';
    }
  }
  const weight = lookFontWeight(fields.fontWeight);
  if (weight !== undefined) out.fontWeight = weight;
  return out;
}
